'use client'
import * as React from 'react'
import { trackRegistrationModalOpen, AuthModalLocations } from '@repo/analytics/amplitude/utils'
import { useIsLoggedInQuery } from 'auth/hooks'
import { clientSecrets } from 'payments/payments-dialog/lib/client-secrets'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { usePaymentsDialog } from 'payments/components/payments-dialog-wrapper-context'
import { useStartFreeTrial } from 'payments/start-free-trial-button/hooks/use-start-free-trial'
import { CF_PAYMENTS_MODAL_CLOSED_EVENT_NAME } from 'payments/payments-dialog/components/close-button'
import { AppRoutes } from '@repo/web-utils/app-routes'
import {
  useHasAllAccessQuery,
  useHasStudioAiQuery,
  useUser,
} from '@repo/cf-user/hooks/use-user-data'
import { AnimatedSpinnerIcon } from 'primitives/icons/animated-spinner-icon'
import type { Variant } from '../config'

const handleSubscriptionComplete = (
  event: CustomEvent<{
    intent_status: string
  }>,
) => {
  if (event.detail.intent_status === 'succeeded' || event.detail.intent_status === 'processing') {
    //redirect to homepage
    window.location.href = '/'
  }
}

const handleCustomPaymentsMessagesOnClose = (
  event: CustomEvent<{
    message: string
  }>,
) => {
  if (event.detail.message === 'free_trial_already_active') {
    //redirect to subscription
    window.location.href = AppRoutes.SUBSCRIPTION
  }
}

function decidePaymentsModalDefaultPlan(variant: Variant) {
  if (variant === 'freeTrialAiStudio' || variant === 'aiStudio') {
    return 'aiStudio'
  } else if (variant === 'yearly' || variant === 'yearlyPromo') {
    return 'yearly'
  }
  return 'freeTrial'
}

export function SidePanelButton({ label, variant }: { label: string; variant: Variant }) {
  const user = useUser()
  const { data: userHasAllAccess } = useHasAllAccessQuery()
  const { data: userHasStudioAi } = useHasStudioAiQuery()

  const [shouldShowAuthModal, setShouldShowAuthModal] = React.useState(true)

  const isLoggedInQuery = useIsLoggedInQuery()
  const isAuthenticated = isLoggedInQuery.data

  const isAIStudioVariant = variant === 'freeTrialAiStudio' || variant === 'aiStudio'
  const { openModal } = useUserAuthModal()
  const { open } = usePaymentsDialog()
  const { startFreeTrial, isPending } = useStartFreeTrial({
    subscriptionType: isAIStudioVariant ? 'studio-ai' : 'all-access',
  })

  const paymentsModalDefaultPlan = decidePaymentsModalDefaultPlan(variant)

  const openAuthModal = React.useCallback(() => {
    trackRegistrationModalOpen(AuthModalLocations.CART)
    //Open register modal and send the payments modal open function as a callback to be executed after successful registration/login
    openModal(undefined, {
      successCallback: () => {
        open(paymentsModalDefaultPlan)
      },
    })
  }, [openModal, open, paymentsModalDefaultPlan])

  const handleButtonClick = async () => {
    if (!isAuthenticated) {
      openAuthModal()
      return null
    }

    if (
      variant === 'freeTrial' ||
      variant === 'freeTrialYearly' ||
      variant === 'freeTrialAiStudio'
    ) {
      await startFreeTrial()
      return null
    }

    open(paymentsModalDefaultPlan)
  }

  React.useEffect(() => {
    if (isAuthenticated === false && !isLoggedInQuery.isPending && shouldShowAuthModal) {
      setShouldShowAuthModal(false)
      openAuthModal()
    }
  }, [
    isAuthenticated,

    openAuthModal,
    isLoggedInQuery.isPending,
    shouldShowAuthModal,
    setShouldShowAuthModal,
  ])

  React.useEffect(() => {
    if (clientSecrets.paymentIntentClientSecret || clientSecrets.setupIntentClientSecret) {
      return
    }

    if (userHasStudioAi && (variant === 'freeTrialAiStudio' || variant === 'aiStudio')) {
      window.location.href = AppRoutes.STUDIO_TOOL
      return
    }

    //If user has all access and the page is free-trial redirect to subscription page
    if (
      userHasAllAccess &&
      (variant === 'freeTrial' || variant === 'monthly' || variant === 'freeTrialYearly')
    ) {
      window.location.href = AppRoutes.SUBSCRIPTION
    }

    //if user is not eligible for yearly upgrade and the page is yearly or yearly promo redirect to subscription page
    if (
      (variant === 'yearly' || variant === 'yearlyPromo') &&
      isAuthenticated &&
      userHasAllAccess &&
      user.data?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade === false
    ) {
      window.location.href = AppRoutes.SUBSCRIPTION
    }
  }, [
    userHasAllAccess,
    userHasStudioAi,
    variant,
    isAuthenticated,
    user.data?.subscriptionAccess.allAccess.isEligibleForYearlyUpgrade,
  ])

  React.useEffect(() => {
    document.addEventListener(
      'cf_intent_status_updated',
      handleSubscriptionComplete as EventListener,
    )

    return () => {
      document.removeEventListener(
        'cf_intent_status_updated',
        handleSubscriptionComplete as EventListener,
      )
    }
  }, [])

  React.useEffect(() => {
    document.addEventListener(
      CF_PAYMENTS_MODAL_CLOSED_EVENT_NAME,
      handleCustomPaymentsMessagesOnClose as EventListener,
    )

    return () => {
      document.removeEventListener(
        CF_PAYMENTS_MODAL_CLOSED_EVENT_NAME,
        handleCustomPaymentsMessagesOnClose as EventListener,
      )
    }
  }, [])

  return (
    <button
      data-testid="side-panel-button"
      onClick={handleButtonClick}
      disabled={isPending}
      className="bg-secondary-500 hover:bg-secondary-700 text-service-white text-14 relative mb-[10px] flex h-[45px] w-full items-center justify-center rounded font-bold uppercase leading-[1.2] tracking-[0.9px]"
    >
      <div className={isPending ? 'invisible' : 'visible'}>{label}</div>
      {isPending ? (
        <span className="absolute inset-0 flex items-center justify-center">
          <AnimatedSpinnerIcon />
        </span>
      ) : null}
    </button>
  )
}
